import React, { useEffect } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { FaRegCircleQuestion } from 'react-icons/fa6';
import styled, { css } from 'styled-components';

import IconButtonModal from '~/shared/components/IconButtonModal';
import MemberCreditCallout from '~/shared/components/MemberCreditCallout';
import { PromoMessage as BasePromoMessage } from '~/shared/components/PromoMessage';
import TariffTooltip from '~/shared/components/TariffTooltip';
import featureNames from '~/shared/constants/featureFlags';
import { MemberDetails } from '~/shared/constants/memberDetails';
import { SecondarySites } from '~/shared/constants/secondarySites';
import {
  TARIFF_MIN_PRICE_THRESHOLD,
  DISCLOSURE_VIEWED_SESSION_DETAIL,
  FLAT_RATE_TARIFF_ELIGIBLE_SESSION_DETAIL,
} from '~/shared/constants/tariff';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import { getPromoPricingActiveAsset } from '~/shared/utils/getPromoPricingActiveAsset';
import { getPromoPricingUIAssetContainerName } from '~/shared/utils/promoPricingUI';
import useEMPPricing from '~/shared/utils/useEMPPricing';
import useGshop from '~/shared/utils/useGshop';
import useHasFeature from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import {
  useMembership,
  useMembershipDetail,
} from '~/techstyle-shared/react-accounts';
import { useAsset } from '~/techstyle-shared/react-assets';
import {
  ProductPrice,
  useProductContext,
  mobile,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { Currency, FormattedMessage } from '~/techstyle-shared/react-intl';
import {
  useDomain,
  useSession,
  useSessionActions,
} from '~/techstyle-shared/redux-core';

import useLoyaltyPricing from '../../utils/useLoyaltyPricing';
import useOutfitSavingsBanner from '../../utils/useOutfitSavingsBanner';
import OutfitSavingsBanner from '../OutfitSavingsBanner';
import { PromoPricingMessage } from '../PromoPricingMessage';

const Wrapper = styled.div``;

const priceStyle = css`
  ${({ theme }) => theme.productDetailPrice.priceStyle};
`;

const PromoMessage = styled(BasePromoMessage)`
  display: inline-block;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.promoText};
  ${({ $promoMessageWrapperStyle }) => $promoMessageWrapperStyle};
  ${({ theme }) => theme.pdpStyles.promoMessage.copy};
`;

const PriceContent = styled.div`
  color: ${({ theme }) => theme.colors.textDefault};
  margin-bottom: 12px;
  & + & {
    margin-bottom: 0px;
  }

  ${({ theme }) => theme.productDetailPrice.priceContent};
  ${({ $priceContentStyles }) => $priceContentStyles};
`;

const PriceContentInner = styled.div`
  display: flex;
  width: 76%;
`;

const PriceText = styled.span`
  ${({ $isGshopMSRPDiscountActive, theme }) =>
    $isGshopMSRPDiscountActive &&
    theme.pdpStyles.promoMessage.gshopMsrpMemberPriceText}
`;

const ProductPriceWrapper = styled.div`
  flex: 1;
  justify-content: flex-start;

  ${mobile`
    padding: 0 0 0 1px;
  `};
`;

const OutletProductPrice = styled(Currency)`
  font-weight: 700;
`;

const promoMessageStyle = css`
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: ${-0.4 / 16}em;
  margin-right: 4px;
  word-break: break-all;

  ${({ theme }) => theme.pdpStyles.promoMessage.copy};
`;

const promoPriceStyle = css`
  ${({ theme }) => theme.pdpStyles.promoMessage.copy};
`;

const gshopProductPricePromoStyle = css`
  ${({ theme }) => theme.pdpStyles.promoMessage.gshopPriceText}
`;

const strikethroughPriceStyle = css`
  ${({ theme }) => theme.pdpStyles.promoMessage.strikethroughCopy};
`;

const TokenOnlyPrice = styled.div`
  color: ${({ theme }) => theme.colors.palette.black};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const OutfitSavingsBannerWrapper = styled.div`
  margin: 24px 0 8px;

  ${mobile`
    margin: -16px 0 32px;
  `};
`;

const LoyaltyPrice = styled.div`
  color: ${({ theme }) => theme.colors.loyaltyPrice};
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 3px;
`;

const PromoWrapper = styled.div`
  margin-bottom: 16px;

  ${({ $hasAccompanyingIconButtonModal }) =>
    $hasAccompanyingIconButtonModal &&
    css`
      display: flex;
      align-items: center;
      gap: 6px;
    `}
`;

const PromoMessageWrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.promoMessage.wrapper};
`;

const VATDisclosure = styled.div`
  ${({ theme }) =>
    theme.typography.variants.productDetailVatDisclaimer.textStyle};
`;

const TokenOnlyPriceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const tokenOnlySlashedOutPriceStyle = css`
  font-size: 14px;
  font-weight: 600;
`;

const tokenOnlyTariffSurchargeStyle = css`
  margin-top: 8px;
`;

const MysteryBoxCallout = styled.span`
  display: inline-block;

  ${({ theme }) => theme.mysteryBox.finalSaleCallout};
`;

/**
 * @function VipOptInIcon
 * @description Provides the icon to display to the user which triggers the display of a modal
 * when clicked
 * @returns {JSX.Element}
 */
function VipOptInIcon() {
  const { isMobile } = useBreakpoint();

  return <FaRegCircleQuestion size={isMobile ? 18 : 20} />;
}

/**
 * @function VipOptInHeader
 * @description Provides header content for use in the modal accompanying the product price.
 * This is currently only in use for the enhanced VIP Opt-In feature.
 * @returns {JSX.Element}
 * TODO: FLCX-3775 will update the `defaultMessage` prop once copy is received
 */
function VipOptInHeader() {
  return (
    <FormattedMessage
      id="vip_opt_in.pdp_pop_up_header"
      defaultMessage="Header content"
    />
  );
}

/**
 * @function VipOptInBody
 * @description Provides body content for use in the modal accompanying the product price.
 * This is currently only in use for the enhanced VIP Opt-In feature.
 * @returns {JSX.Element}
 * TODO: FLCX-3775 will update the `defaultMessage` prop once copy is received
 */
function VipOptInBody() {
  return (
    <>
      <IconButtonModal.BodySubheader>
        <FormattedMessage
          id="vip_opt_in.pdp_pop_up_subheader_1"
          defaultMessage="Subheader 1"
        />
      </IconButtonModal.BodySubheader>
      <IconButtonModal.BodyCopy>
        <FormattedMessage
          id="vip_opt_in.pdp_pop_up_paragraph_1"
          defaultMessage="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
            sed cursus nisl. Maecenas rhoncus convallis mollis. Sed vel mi quis
            leo vestibulum fermentum. Praesent odio mi, porta ut justo nec,
            sagittis venenatis dolor."
        />
      </IconButtonModal.BodyCopy>

      <IconButtonModal.BodySubheader>
        <FormattedMessage
          id="vip_opt_in.pdp_pop_up_subheader_2"
          defaultMessage="Subheader 2"
        />
      </IconButtonModal.BodySubheader>
      <IconButtonModal.BodyCopy>
        <FormattedMessage
          id="vip_opt_in.pdp_pop_up_paragraph_2"
          defaultMessage="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
            sed cursus nisl. Maecenas rhoncus convallis mollis. Sed vel mi quis
            leo vestibulum fermentum. Praesent odio mi, porta ut justo nec,
            sagittis venenatis dolor."
        />
      </IconButtonModal.BodyCopy>

      <IconButtonModal.BodySubheader>
        <FormattedMessage
          id="vip_opt_in.pdp_pop_up_subheader_3"
          defaultMessage="Subheader 3"
        />
      </IconButtonModal.BodySubheader>
      <IconButtonModal.BodyCopy>
        <FormattedMessage
          id="vip_opt_in.pdp_pop_up_paragraph_3"
          defaultMessage="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
            sed cursus nisl. Maecenas rhoncus convallis mollis. Sed vel mi quis
            leo vestibulum fermentum. Praesent odio mi, porta ut justo nec,
            sagittis venenatis dolor."
        />
      </IconButtonModal.BodyCopy>
    </>
  );
}

const vatDisclaimerEnabledTlds = config.get(
  'public.brand.vatDisclaimerEnabledTlds'
);

const ProductDetailPrice = ({
  product: productFromProps,
  priceContentStyles,
  showEMPPrice = true,
  promoMessageWrapperStyle,
  promoMessageProps,
  ...rest
}) => {
  const { enabled: isPrioritizedFeatureEnabled } = useHasFeature(
    featureNames.PRIORITIZED_PROMOS
  );
  const { enabled: displayRetailPrice } = useHasFeature(
    featureNames.RETAILPRICETEST
  );
  const { enabled: isSingleCtaVipOptInEnabled } = useHasFeature(
    featureNames.SINGLE_CTA_VIP_OPT_IN
  );
  const { enabled: tariffLeadFeatureEnabled } = useHasFeature(
    featureNames.TARIFF
  );
  const { enabled: tariffVipFeatureEnabled } = useHasFeature(
    featureNames.TARIFF_VIP
  );
  const showPromoGuestPrice = config.get(
    'public.brand.features.showPromoGuestPrice'
  );

  const { tld } = useDomain();
  const { isMobile } = useBreakpoint();
  const productFromContext = useProductContext();
  const product = productFromProps || productFromContext;
  const { promoCode, promoGuestPrice } = product?.promos || {};
  const hasPromo = Boolean(promoCode) && typeof promoCode === 'string';
  const { isLoggedIn } = useSession();
  const promoPricingConfig = config.get('public.brand.promoPricingUI');
  const sessionActions = useSessionActions();
  const { isVip, isLead } = useMembership();
  const isPrioritizedEnabled =
    isPrioritizedFeatureEnabled && promoPricingConfig?.showPromoPricingOffer;
  const offerLabelContainerName = getPromoPricingUIAssetContainerName(
    promoPricingConfig,
    isVip
  );
  const assetContainerOfferLabel = useAsset(offerLabelContainerName);
  const promoPricingActiveAsset = getPromoPricingActiveAsset({
    isPrioritizedEnabled,
    assetContainerOfferLabel,
    product,
  });
  const isPromoPricingActive = Boolean(promoPricingActiveAsset);
  const pdpLabel = promoPricingActiveAsset?.options?.customVars?.pdpLabel;
  const htmlText = promoPricingActiveAsset?.options?.htmlText;
  const { isGshopActive, isGshopMSRPDiscountActive } = useGshop();
  const showVipPrice =
    (!isLoggedIn && !displayRetailPrice) ||
    isVip ||
    (isLead && !displayRetailPrice);
  const { isTokenOnly, showMemberCredits, tokenValue } = useEMPPricing(product);
  const { isLoyaltyItem, productLoyaltyValue } = useLoyaltyPricing(product);
  const showTokenPricing = isTokenOnly && !isLoyaltyItem;
  const { showSavingsBanner, outfitValue, creditCount, savingsAmount } =
    useOutfitSavingsBanner(product);
  const showRegularPricing = !isTokenOnly && !isLoyaltyItem;
  const secondarySite = useSecondarySite();
  const retailUnitPrice = parseFloat(product.retailUnitPrice);
  const isTariffFeatureEnabled =
    tariffLeadFeatureEnabled || tariffVipFeatureEnabled;
  const meetsTariffPriceThreshold =
    retailUnitPrice >= TARIFF_MIN_PRICE_THRESHOLD;
  const isCmsTariffDisclosureEligible =
    isTariffFeatureEnabled && meetsTariffPriceThreshold;
  const isSortingHatTariffDisclosureEligible = Boolean(
    product?.tariffDetails?.displayDisclosure
  );
  const featureEligible =
    isCmsTariffDisclosureEligible || isSortingHatTariffDisclosureEligible;
  const userMeetsTariffCriteria = getBrandValue({
    fabletics: isLead,
    default: true,
  });
  const skipSurchargeMembershipDetailRecord = useMembershipDetail(
    MemberDetails.SKIP_SURCHARGE
  );
  const shouldHideTariffDisclosure =
    !featureEligible ||
    userMeetsTariffCriteria ||
    skipSurchargeMembershipDetailRecord?.value === '1';
  const tariffEligibleZeroRate =
    product?.tariffDetails?.calcMethod === 'percentage' &&
    product?.tariffDetails?.rate === 0 &&
    meetsTariffPriceThreshold;
  const displayVATDisclaimer = vatDisclaimerEnabledTlds.includes(tld);
  const isMysteryBox = product?.isMysteryBundle;

  const showGuestPrice =
    showPromoGuestPrice &&
    !!promoGuestPrice &&
    promoGuestPrice !== retailUnitPrice;

  useEffect(() => {
    if (shouldHideTariffDisclosure) {
      sessionActions.updateSessionDetail({
        name: DISCLOSURE_VIEWED_SESSION_DETAIL.NAME,
        value: DISCLOSURE_VIEWED_SESSION_DETAIL.VALUES.DISCLOSURE_HIDDEN,
      });
    }
    if (tariffEligibleZeroRate) {
      sessionActions.updateSessionDetail({
        name: FLAT_RATE_TARIFF_ELIGIBLE_SESSION_DETAIL.NAME,
        value: FLAT_RATE_TARIFF_ELIGIBLE_SESSION_DETAIL.VALUES.ELIGIBLE,
      });
    }
  }, [shouldHideTariffDisclosure, tariffEligibleZeroRate, sessionActions]);

  if (secondarySite === SecondarySites.OUTLET) {
    return (
      <Wrapper>
        <PriceContent $priceContentStyles={priceContentStyles} tabIndex={0}>
          <PriceContentInner>
            <PriceText>
              <FormattedMessage
                id="site_product_page.outlet_price"
                defaultMessage="Outlet Price"
              />
              :&nbsp;
            </PriceText>
            <ProductPriceWrapper>
              <OutletProductPrice
                amount={product.warehouseUnitPrice}
                data-autotag="product-outlet-price"
              />
            </ProductPriceWrapper>
          </PriceContentInner>
          <PriceContentInner>
            <PriceText>
              <FormattedMessage
                id="site_product_page.outlet_msrp"
                defaultMessage="MSRP"
              />
              :&nbsp;
            </PriceText>
            <ProductPriceWrapper>
              <OutletProductPrice
                amount={retailUnitPrice}
                data-autotag="product-outlet-msrp"
              />
            </ProductPriceWrapper>
          </PriceContentInner>
        </PriceContent>
      </Wrapper>
    );
  }

  return (
    <Wrapper {...rest}>
      {isLoyaltyItem && (
        <>
          <LoyaltyPrice tabIndex={0}>
            <FormattedMessage
              id="site_product_page.pdp_loyalty_price"
              defaultMessage="Points:"
            />{' '}
            {productLoyaltyValue}
          </LoyaltyPrice>
        </>
      )}
      {isMobile && showSavingsBanner && (
        <OutfitSavingsBannerWrapper>
          <OutfitSavingsBanner
            outfitValue={outfitValue}
            creditCount={creditCount}
            savingsAmount={savingsAmount}
          />
        </OutfitSavingsBannerWrapper>
      )}
      {showTokenPricing && (
        <TokenOnlyPriceWrapper>
          <TokenOnlyPrice tabIndex={0}>
            <FormattedMessage
              id="site_pdp.pdp_token_only_price"
              defaultMessage="VIP:"
            />{' '}
            {showMemberCredits && (
              <MemberCreditCallout
                product={product}
                tokenValue={tokenValue}
                isTokenOnly
              />
            )}
          </TokenOnlyPrice>
          <ProductPrice
            priceStyle={tokenOnlySlashedOutPriceStyle}
            showRetailPrice={false}
            strikeRegularPrice
            showVipPrice
            showPromoPrice={false}
            data-autotag="pdp-reg-price"
          />
          {isMysteryBox && (
            <MysteryBoxCallout>
              <FormattedMessage
                id="site_pdp.mystery_box_final_sale_callout"
                defaultMessage="Final Sale"
              />
            </MysteryBoxCallout>
          )}
        </TokenOnlyPriceWrapper>
      )}
      {showRegularPricing && (
        <>
          {hasPromo && (
            <PromoWrapper
              $hasAccompanyingIconButtonModal={isSingleCtaVipOptInEnabled}
            >
              <PromoMessageWrapper>
                {isPromoPricingActive ? (
                  <PromoPricingMessage
                    promoPriceStyle={promoPriceStyle}
                    pdpLabel={pdpLabel}
                    priceStyle={strikethroughPriceStyle}
                    htmlText={htmlText}
                    product={product}
                    tabIndex={0}
                  />
                ) : (
                  <PromoMessage
                    promoMessageStyle={promoMessageStyle}
                    promoPriceStyle={promoPriceStyle}
                    data-autotag="pdp-promo-message"
                    $promoMessageWrapperStyle={promoMessageWrapperStyle}
                    tabIndex={0}
                    {...promoMessageProps}
                  />
                )}
              </PromoMessageWrapper>

              {isSingleCtaVipOptInEnabled && (
                <IconButtonModal
                  headerContent={VipOptInHeader}
                  bodyContent={VipOptInBody}
                  icon={VipOptInIcon}
                />
              )}

              {showEMPPrice && showMemberCredits && (
                <MemberCreditCallout
                  product={product}
                  tabIndex={0}
                  tokenValue={tokenValue}
                />
              )}
            </PromoWrapper>
          )}

          {showVipPrice && (
            <PriceContent $priceContentStyles={priceContentStyles} tabIndex={0}>
              <FormattedMessage
                id="site_product_page.vip"
                defaultMessage="VIP"
              />
              :&nbsp;
              <ProductPrice
                showPromoPrice={false}
                showVipPrice
                priceStyle={priceStyle}
                data-autotag="pdp-vip-price"
              />
              {!hasPromo && showEMPPrice && showMemberCredits ? (
                <MemberCreditCallout
                  product={product}
                  tokenValue={tokenValue}
                />
              ) : null}
              {isMysteryBox && (
                <MysteryBoxCallout>
                  <FormattedMessage
                    id="site_pdp.mystery_box_final_sale_callout"
                    defaultMessage="Final Sale"
                  />
                </MysteryBoxCallout>
              )}
            </PriceContent>
          )}
          {showGuestPrice ? (
            <PriceContent $priceContentStyles={priceContentStyles} tabIndex={0}>
              <PriceContentInner>
                <PriceText isPDPPromoAvailable={isPromoPricingActive}>
                  <FormattedMessage
                    id="site_product_page.gshop_promo"
                    defaultMessage="NOW"
                  />
                  :&nbsp;
                </PriceText>
                <ProductPriceWrapper>
                  <ProductPrice
                    promoPriceStyle={gshopProductPricePromoStyle}
                    showPromoGuestPrice
                    showVipPrice={false}
                    priceStyle={priceStyle}
                    showPromoPrice={false}
                    data-autotag="pdp-gshop-price"
                  />
                </ProductPriceWrapper>
              </PriceContentInner>
            </PriceContent>
          ) : null}
          {!isMysteryBox && (
            <PriceContent $priceContentStyles={priceContentStyles} tabIndex={0}>
              <PriceContentInner>
                <PriceText
                  $isGshopMSRPDiscountActive={isGshopMSRPDiscountActive}
                  isPDPPromoAvailable={isPromoPricingActive}
                >
                  {isGshopActive ? (
                    <FormattedMessage
                      id="site_product_page.gshop"
                      defaultMessage="TODAY"
                    />
                  ) : (
                    <FormattedMessage
                      id="site_product_page.retail"
                      defaultMessage="Non-Member"
                    />
                  )}
                  :&nbsp;
                </PriceText>
                <ProductPriceWrapper>
                  <ProductPrice
                    priceStyle={css`
                      ${priceStyle}
                      ${({ theme }) => {
                        if (isGshopMSRPDiscountActive) {
                          return theme.pdpStyles.promoMessage
                            .gshopMsrpMemberPriceText;
                        }
                      }}
                    `}
                    showRetailPrice={
                      isGshopMSRPDiscountActive ? true : !isGshopActive
                    }
                    strikeRegularPrice={false}
                    showVipPrice={
                      isGshopMSRPDiscountActive ? false : isGshopActive
                    }
                    showPromoPrice={false}
                    data-autotag="pdp-reg-price"
                  />
                </ProductPriceWrapper>
              </PriceContentInner>
            </PriceContent>
          )}
          {isMysteryBox && (
            <MysteryBoxCallout>
              <FormattedMessage
                id="site_pdp.mystery_box_final_sale_callout"
                defaultMessage="Final Sale"
              />
            </MysteryBoxCallout>
          )}
        </>
      )}
      {displayVATDisclaimer && (
        <VATDisclosure>
          <FormattedMessage
            id="site_pdp.vat_disclosure"
            defaultMessage="*Inkl. Mwst., versandkostenfrei ab 49.95 €."
          />
        </VATDisclosure>
      )}
      {!shouldHideTariffDisclosure && (
        <TariffTooltip
          tariffCalloutStyle={
            showTokenPricing ? tokenOnlyTariffSurchargeStyle : css``
          }
        />
      )}
      {!isMobile && showSavingsBanner && (
        <OutfitSavingsBannerWrapper>
          <OutfitSavingsBanner
            outfitValue={outfitValue}
            creditCount={creditCount}
            savingsAmount={savingsAmount}
          />
        </OutfitSavingsBannerWrapper>
      )}
    </Wrapper>
  );
};

ProductDetailPrice.propTypes = {
  priceContentStyles: PropTypes.any,
  product: PropTypes.object,
  promoMessageProps: PropTypes.object,
  promoMessageWrapperStyle: PropTypes.any,
  showEMPPrice: PropTypes.bool,
};

export default ProductDetailPrice;
